import {Logger} from '../../../common/utils/logger';
import {MapType} from '../../enums/map-type';
import {ObjectUtil} from '../../../common/utils/object.util';

export abstract class BaseTileProvider implements google.maps.MapType {

  public static MAX_ZOOM = 27;
  public static TILE_SIZE = 256;

  protected mapType: string;
  tileSize = new google.maps.Size(BaseTileProvider.TILE_SIZE, BaseTileProvider.TILE_SIZE);
  maxZoom = BaseTileProvider.MAX_ZOOM;
  minZoom = 2;
  name = this.getMapProvider();
  alt = 'Tile Coordinate Map Type';
  radius: number;
  projection: google.maps.Projection | null;

  private static MAX_LOADED_URLS = 4096;

  // The tiles Google currently shows, so refreshAll() can redraw each one.
  // Keyed by element, not URL: overzoomed tiles share their parent's URL.
  private tileElements = new Set<Element>();

  // The image each tile is waiting for. A download that finishes after its
  // tile was redrawn or released is dropped instead of covering the new one.
  private tileImages = new WeakMap<Element, HTMLImageElement>();

  // Tile URLs that finished downloading, oldest first. Overzoomed tiles look
  // here for a parent to stretch while their own image loads; probing with a
  // new <img> would start a download for every level it tries.
  private loadedUrls = new Set<string>();

  /**
   * How far (CSS px) each tile's picture reaches into its neighbours'. Between
   * zoom levels Google scales the tiles, their edges fall between device
   * pixels and come out half transparent, and the map showed a grid of light
   * seams on the imagery (plainly at 150% screen scaling). Overlapping by half
   * a pixel on every side closes them; the picture is stretched by 1/256.
   */
  private static readonly SEAM_OVERLAP = 0.5;

  /**
   * The style of an image larger than its tile (a parent level's, stretched):
   * moved so this tile's part of it is over the tile, and cropped to that part
   * and the overlap. It was cropped by the tile's overflow, which left no
   * room for the overlap.
   */
  private static getTileStyle(xOffset, yOffset, tileSize, imgSize) {
    const left = xOffset * tileSize;
    const top = yOffset * tileSize;
    const bottom = imgSize - top - tileSize;
    const right = imgSize - left - tileSize;
    const o = BaseTileProvider.SEAM_OVERLAP;
    const transform = 'translateX(' + (-left) + 'px) translateY(' + (-top) + 'px);';
    return 'transform-origin: 0 0;'
      + 'transform: ' + transform
      + 'clip-path: inset(' + (top - o) + 'px ' + (right - o) + 'px ' + (bottom - o) + 'px ' + (left - o) + 'px);'
      + 'margin: 0;'
      + 'padding: 0;'
      + 'display: inline-block;';
  }

  abstract getMapProvider(): string;

  abstract getMaxZoom(): number;

  abstract getUrl(x, y, zoom, mapType?: MapType): string;

  abstract getUrlTemplate(mapType?: MapType): string;

  releaseTile(tile: Element): void {
    this.tileElements.delete(tile);
    this.tileImages.delete(tile);
  }

  getTile(tileCoord: google.maps.Point | null, zoom: number, ownerDocument: Document | null): Element | null {
    const div = this.getTileDiv(ownerDocument, tileCoord.x, tileCoord.y, zoom);
    this.tileElements.add(div);
    return div;
  }

  getTileDiv(ownerDocument, x, y, zoom): Element {
    const div = ownerDocument.createElement('div');
    return this.getTileDivInternal(div, x, y, zoom);
  }

  getTileDivInternal(div, x, y, zoom): Element {

    try {

      div.innerHTML = '';
      // A download still running for the previous drawing must not land here.
      this.tileImages.delete(div);
      div.setAttribute('x', x);
      div.setAttribute('y', y);
      div.setAttribute('z', zoom);

      zoom = Math.round(zoom);

      const dim = Math.pow(2, zoom);

      /*if(y<0){
          var nRows = (1 << zoom); // Num rows = 2^level
          y = nRows - 1 - y;
      }
      if(x<0){
          var nCols = (1 << zoom); // Num rows = 2^level
          x = nCols - 1 - x;
      }*/

      // Data for request. Google hands custom map types unwrapped columns
      // (negative west of -180, 2^zoom and up past +180), so wrap them onto
      // the one world; mirroring them with Math.abs showed the wrong ground.
      x = ((x % dim) + dim) % dim;

      // Logger.log('X=' + x + '    Y=' + y + '   Z=' + zoom + '    DIM=' + dim);

      const th = BaseTileProvider.TILE_SIZE;
      const tw = BaseTileProvider.TILE_SIZE;


      div.style.width = tw + 'px';
      div.style.height = th + 'px';
      // Each picture crops itself (getTileStyle), so that it can reach over
      // the seam into the next tile (SEAM_OVERLAP).
      div.style.overflow = 'visible';
      div.style.position = 'relative';
      div.style.border = '0';
      /*div.style.padding = '0';
       div.style.margin = '0';
       div.style.fontSize = '10';
       div.style.borderWidth = '0';
       div.style.borderColor = '#00000000';*/
      div.style.backgroundColor = '#00000000';

      // Rows do not wrap: nothing lies above or below the projection, so
      // leave the tile empty rather than repeat the world vertically.
      if (y < 0 || y >= dim) {
        div.removeAttribute('url');
        return div;
      }

      let url = this.getUrl(x, y, zoom);

      const maxTileZoom = this.getMaxZoom();

      /*var backTile;
       if (!cached(url)) {
       backTile = getCachedParentTile(maptype, ownerDocument, x, y, zoom);
       if (backTile) {
       div.appendChild(backTile);
       }
       }*/
      const img = document.createElement('img');

      if (zoom > maxTileZoom) {

        const backTile = this.getCachedParentTile(document, x, y, zoom);
        if (backTile) {
          div.appendChild(backTile);
        }

        let newX = x;
        let newY = y;
        let evenX;
        let evenY;
        let scale = 0;
        let xOffset = 0;
        let yOffset = 0;
        let imgSize = BaseTileProvider.TILE_SIZE;
        for (let z = zoom; z > maxTileZoom; z--) {
          evenX = newX % 2 === 0;
          evenY = newY % 2 === 0;
          newX = evenX ? newX / 2 : (newX - 1) / 2;
          newY = evenY ? newY / 2 : (newY - 1) / 2;

          if (!evenX) {
            xOffset += (Math.pow(2, scale));
          }
          if (!evenY) {
            yOffset += (Math.pow(2, scale));
          }

          imgSize *= 2;
          scale++;
        }

        url = this.getUrl(newX, newY, maxTileZoom);

        const style = BaseTileProvider.getTileStyle(xOffset, yOffset, BaseTileProvider.TILE_SIZE, imgSize);
        img.height = imgSize;
        img.width = imgSize;
        img.style.cssText = style + 'position: absolute; top: 0; left: 0; z-index: 2; border:0;';

        this.loadTileImage(div, img, url);

        /*$.ajax({
        url : url,
        cache: true,
        processData : false
        }).always(function(){
        $(img).attr("src", url).fadeIn();
        });*/
      } else {
        img.height = th;
        img.width = tw;
        // Grown from its centre over the seams (SEAM_OVERLAP).
        const grow = 1 + 2 * BaseTileProvider.SEAM_OVERLAP / tw;
        img.style.cssText = `position: absolute; z-index: 2; top: 0; left: 0; border:0; transform: scale(${grow});`;

        this.loadTileImage(div, img, url);
      }

      div.setAttribute('url', url);

    } catch (err) {
      // Logger.error(err);
    }


    return div;
  }

  refreshAll(){
    this.tileElements.forEach(t => {
      this.getTileDivInternal(t, Number(t.getAttribute('x')), Number(t.getAttribute('y')), Number(t.getAttribute('z')))
    })
  }

  clearCache() {
    this.tileElements.clear();
    this.tileImages = new WeakMap<Element, HTMLImageElement>();
    this.loadedUrls.clear();
  }

  /**
   * Downloads a tile image and shows it in its tile once loaded, unless the
   * tile was redrawn (map type or source switched) or released meanwhile, so
   * a slow download for the old layer cannot land on top of the new one.
   */
  private loadTileImage(div: Element, img: HTMLImageElement, url: string) {
    this.tileImages.set(div, img);
    const downloadingImage = new Image();
    downloadingImage.onload = () => {
      this.rememberLoaded(url);
      if (this.tileImages.get(div) !== img) {
        return;
      }
      img.src = downloadingImage.src;
      div.appendChild(img);
    };
    // downloadingImage.onerror = tileDownloadFailed;
    downloadingImage.src = url;
  }

  private rememberLoaded(url: string) {
    // Re-adding moves it to the newest end, so the oldest is dropped first.
    this.loadedUrls.delete(url);
    this.loadedUrls.add(url);
    if (this.loadedUrls.size > BaseTileProvider.MAX_LOADED_URLS) {
      const oldest = this.loadedUrls.values().next().value;
      if (oldest !== undefined) {
        this.loadedUrls.delete(oldest);
      }
    }
  }

  getCachedParentTile(ownerDocument, x, y, zoom) {
    // Only levels the source has: anything above its max zoom does not
    // exist, and asking for it would cost a failed request per tile.
    const maxTileZoom = this.getMaxZoom();
    let newX = x;
    let newY = y;
    let evenX;
    let evenY;
    let scale = 0;
    let xOffset = 0;
    let yOffset = 0;
    let imgSize = BaseTileProvider.TILE_SIZE;
    for (let z = zoom - 1; z > 0; z--) {
      evenX = newX % 2 === 0;
      evenY = newY % 2 === 0;
      newX = evenX ? newX / 2 : (newX - 1) / 2;
      newY = evenY ? newY / 2 : (newY - 1) / 2;

      if (!evenX) {
        xOffset += (Math.pow(2, scale));
      }
      if (!evenY) {
        yOffset += (Math.pow(2, scale));
      }

      imgSize *= 2;
      scale++;

      if (z > maxTileZoom) {
        continue;
      }

      const url = this.getUrl(newX, newY, z);
      if (this.loadedUrls.has(url)) {
        const tile: HTMLImageElement = ownerDocument.createElement('img');
        const style = BaseTileProvider.getTileStyle(xOffset, yOffset, BaseTileProvider.TILE_SIZE, imgSize);
        tile.height = imgSize;
        tile.width = imgSize;
        tile.style.cssText = style + 'position: absolute; z-index: 1;';
        tile.src = url;
        return tile;
      }

    }
    return null;
  }

  // reloadTiles() {
  //     const tiles = $(app.$.mapcanvas).find('img');
  //     for (let i = 0; i < tiles.length; i++) {
  //         let src = $(tiles[i]).attr('src');
  //         if (/googleapis.com\/vt\?pb=/.test(src)) {
  //             let new_src = src.split('&ts')[0] + '&ts=' + (new Date()).getTime();
  //             $(tiles[i]).attr('src', new_src);
  //         }
  //     }
  // }
}
